import type { InsightCardItem } from '@wix/ai-scheduling-assistant-common-lib/entities';

const FOLLOWUP_INSIGHT_KINDS = new Set(['campaignClickers', 'restockDemand', 'expiringLoyaltyPoints', 'rebookingFollowUp']);

export const isFollowupInsight = (item: Pick<InsightCardItem, 'kind'>): boolean => FOLLOWUP_INSIGHT_KINDS.has(item.kind);

/**
 * Business Manager Home's SEO and traffic cards. Their labels name their own window ("last {days} days", "vs. the
 * {days} days before"), so like the follow-ups they never show the generic window line.
 */
const HOME_SEO_INSIGHT_KINDS = new Set([
  'aiBotVisits',
  'pageTwoSearches',
  'lowCtrPages',
  'searchConnection',
  'searchTrend',
  'siteTraffic',
  'trafficSource',
]);

export const isHomeSeoInsight = (item: Pick<InsightCardItem, 'kind'>): boolean => HOME_SEO_INSIGHT_KINDS.has(item.kind);

/** Kinds with no big number: the card says only that the data is missing. */
const NO_VALUE_KINDS = new Set(['gbpConnection', 'searchConnection']);

const HOME_SEO_DEFAULT_WINDOW_DAYS = 30;

const direction = (item: InsightCardItem): 'up' | 'down' => (item.value > 0 ? 'up' : 'down');

/**
 * The tile's label key, or `undefined` when the item has no label to show: a trafficSource card without its
 * `facts.source` renders without label and detail rather than a raw `{source}`.
 */
export const insightLabelKey = (item: InsightCardItem, locale = 'en'): string | undefined => {
  const kind = `chat.insightCard.kind.${item.kind}`;
  if (isFollowupInsight(item)) {
    // The Home count contract is whole, non-negative Int32. Keep malformed metadata's plural
    // fallback identical to iOS/Android instead of applying different decimal/negative rules.
    const validCount = Number.isInteger(item.value) && item.value >= 0 && item.value <= 2147483647;
    return `${kind}.label.${validCount ? new Intl.PluralRules(locale).select(item.value) : 'other'}`;
  }
  switch (item.kind) {
    case 'aiBotVisits':
      return item.value === 0 ? `${kind}.label.none` : `${kind}.label`;
    case 'pageTwoSearches':
    case 'lowCtrPages':
      return `${kind}.label.${item.value === 1 ? 'one' : 'other'}`;
    case 'trafficSource':
      return item.facts?.source ? `${kind}.label.${direction(item)}` : undefined;
    default:
      return `${kind}.label`;
  }
};

const homeSeoDetailKey = (item: InsightCardItem): string | undefined => {
  const kind = `chat.insightCard.kind.${item.kind}`;
  switch (item.kind) {
    case 'aiBotVisits':
      return item.value === 0 ? `${kind}.detail.none` : `${kind}.detail`;
    case 'searchTrend':
      return item.facts?.clicks !== undefined && item.facts?.impressions !== undefined
        ? `${kind}.detailWithTotals.${direction(item)}`
        : `${kind}.detail.${direction(item)}`;
    case 'siteTraffic':
      return `${kind}.detail.${direction(item)}`;
    case 'trafficSource':
      return item.facts?.source ? `${kind}.detail.${direction(item)}` : undefined;
    default:
      return `${kind}.detail`;
  }
};

/**
 * The placeholders a Home SEO card's label and detail fill: `{days}` (30 when the item names no window), and
 * `{source}`, `{clicks}`, `{impressions}` only when `facts` carries them, since a message missing an argument
 * renders as written.
 */
export const insightCopyParams = (item: InsightCardItem, locale: string): Record<string, string | number> => {
  if (!isHomeSeoInsight(item)) {
    return {};
  }
  const integer = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const facts = item.facts;
  return {
    days: item.windowDays ?? HOME_SEO_DEFAULT_WINDOW_DAYS,
    ...(facts?.source ? { source: facts.source } : {}),
    ...(facts?.clicks !== undefined ? { clicks: integer.format(facts.clicks) } : {}),
    ...(facts?.impressions !== undefined ? { impressions: integer.format(facts.impressions) } : {}),
  };
};

const KNOWN_ROLES = new Set(['sales', 'bookings', 'reviews', 'marketing', 'finance', 'support']);
/** Kinds whose window looks forward ("Next 7 days"); to confirm with symphony-packs-service (plan, Contract requests 3). */
const FORWARD_WINDOW_KINDS = new Set(['planRenewals', 'sessions']);
/** Kinds whose copy has a `.detail` line (plan, "Copy"). The line needs `count`. */
const DETAIL_KINDS = new Set(['carts', 'abandonedBookings', 'unpaidBookings', 'overdueInvoices', 'planRenewals', 'forms', 'productSales']);

const formatUnitValue = (item: InsightCardItem, locale: string): string => {
  if (item.unit === 'percent') {
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(item.value / 100);
  }
  if (item.unit === 'money' && item.currency) {
    // Both fraction bounds: older Safari throws a RangeError on a max below the currency's default min.
    return new Intl.NumberFormat(locale, { style: 'currency', currency: item.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(
      item.value,
    );
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(item.value);
};

/**
 * The tile's big number, by the one rule all three surfaces share (plan, "Value format"). `capped`
 * belongs to `count` when there is one, so the `+` goes on the big number only without a count, and
 * after the unit format, so a capped money or percent value keeps its lower-bound marker too.
 * Follow-ups use this value inside their counted label, where the cap must accompany it.
 */
export const formatInsightValue = (item: InsightCardItem, locale: string): string => {
  if (NO_VALUE_KINDS.has(item.kind)) {
    return '';
  }
  const value = formatUnitValue(item, locale);
  return item.capped && (item.count === undefined || isFollowupInsight(item)) ? `${value}+` : value;
};

/**
 * The card's "As of" date, in UTC: the date the tool result and the accept note quote
 * (`computedAt.slice(0, 10)`), so the card and the reply never name different days near midnight.
 */
export const formatInsightAsOf = (computedAt: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(computedAt));

/** The `{count}` of the detail line: locale digits, with `+` when the count hit the read's cap. */
export const formatInsightCount = (item: InsightCardItem, locale: string): string | undefined => {
  if (item.count === undefined) {
    return undefined;
  }
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(item.count);
  return item.capped ? `${number}+` : number;
};

export const insightWindowKey = (item: InsightCardItem): string | undefined => {
  // The approved Home label/body already names the follow-up and SEO windows, including their direction.
  if (isFollowupInsight(item) || isHomeSeoInsight(item) || item.windowDays === undefined) {
    return undefined;
  }
  return FORWARD_WINDOW_KINDS.has(item.kind) ? `chat.insightCard.kind.${item.kind}.window` : 'chat.insightCard.window';
};

/** Home follow-ups and SEO cards reuse their approved body; other details use the existing one/other convention. */
export const insightDetailKey = (item: InsightCardItem): string | undefined =>
  isFollowupInsight(item)
    ? `chat.insightCard.kind.${item.kind}.detail`
    : isHomeSeoInsight(item)
    ? homeSeoDetailKey(item)
    : item.count !== undefined && DETAIL_KINDS.has(item.kind)
    ? `chat.insightCard.kind.${item.kind}.detail.${item.count === 1 ? 'one' : 'other'}`
    : undefined;

export const insightRoleKey = (role: string): string =>
  `chat.insightCard.role.${role === 'scheduling' ? 'bookings' : KNOWN_ROLES.has(role) ? role : 'other'}`;
